import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { TrendingDown, DollarSign, Users } from 'lucide-react';

export const PatientROICalculator = () => {
  const [patientSessions, setPatientSessions] = useState(8);
  const [traditionalPrice] = useState(120);
  const [platformPrice] = useState(50);

  // Patient Calculations
  const monthlyTraditionalCost = traditionalPrice * patientSessions;
  const monthlyPlatformCost = platformPrice * patientSessions;
  const monthlySavings = monthlyTraditionalCost - monthlyPlatformCost;
  const yearlyTraditionalCost = monthlyTraditionalCost * 12;
  const yearlyPlatformCost = monthlyPlatformCost * 12;
  const yearlySavings = monthlySavings * 12;
  const savingsPercentage = ((monthlySavings / monthlyTraditionalCost) * 100).toFixed(0);

  return (
    <section className="py-16 bg-gradient-to-br from-background to-primary/5">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <Badge className="mb-3">
            <DollarSign className="h-3 w-3 mr-1" />
            Calculate Your Savings
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            See How Much You Can Save
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Compare traditional therapy costs with our global platform
          </p>
        </div>

        <Card className="border-2 border-primary/20 shadow-xl max-w-4xl mx-auto">
          <CardHeader className="border-b bg-gradient-to-r from-primary/5 to-accent/5">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Patient Savings Calculator
            </CardTitle>
            <CardDescription>
              See how much you save choosing our global platform
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-8">
              {/* Sessions Input */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <label className="text-sm font-medium">Sessions per month</label>
                  <Badge variant="secondary" className="text-lg font-bold">
                    {patientSessions}
                  </Badge>
                </div>
                <Slider
                  value={[patientSessions]}
                  onValueChange={([value]) => setPatientSessions(value)}
                  min={1}
                  max={20}
                  step={1}
                  className="mb-2"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>1 session</span>
                  <span>20 sessions</span>
                </div>
              </div>

              {/* Comparison */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Traditional Therapy */}
                <div className="bg-muted/50 rounded-lg p-6 border-2 border-border">
                  <div className="text-sm font-medium text-muted-foreground mb-2">
                    Traditional In-Person Therapy
                  </div>
                  <div className="text-3xl font-bold mb-4">
                    ${monthlyTraditionalCost}
                    <span className="text-sm font-normal text-muted-foreground">/month</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price per session:</span>
                      <span className="font-semibold">${traditionalPrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Yearly cost:</span>
                      <span className="font-semibold">${yearlyTraditionalCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Our Platform */}
                <div className="bg-primary/10 rounded-lg p-6 border-2 border-primary relative overflow-hidden">
                  <Badge className="absolute top-3 right-3 text-xs">
                    Best Value
                  </Badge>
                  <div className="text-sm font-medium text-primary mb-2">
                    Our Global Platform
                  </div>
                  <div className="text-3xl font-bold mb-4 text-primary">
                    ${monthlyPlatformCost}
                    <span className="text-sm font-normal text-muted-foreground">/month</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Price per session:</span>
                      <span className="font-semibold">${platformPrice}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Yearly cost:</span>
                      <span className="font-semibold">${yearlyPlatformCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings Summary */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <TrendingDown className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-green-900">Your Total Savings</div>
                    <div className="text-xs text-green-700">Save {savingsPercentage}% on therapy costs</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Monthly Savings</div>
                    <div className="text-2xl font-bold text-green-600">
                      ${monthlySavings}
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="text-sm text-muted-foreground mb-1">Yearly Savings</div>
                    <div className="text-2xl font-bold text-green-600">
                      ${yearlySavings.toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-4 text-xs text-green-700 text-center">
                  💡 That's like getting {Math.floor(yearlySavings / platformPrice)} free sessions per year!
                </div>
              </div>

              {/* Additional Benefits */}
              <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t">
                <div className="text-center">
                  <div className="text-2xl mb-1">🌍</div>
                  <div className="text-sm font-medium">5,000+ Therapists</div>
                  <div className="text-xs text-muted-foreground">Worldwide access</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">💬</div>
                  <div className="text-sm font-medium">40+ Languages</div>
                  <div className="text-xs text-muted-foreground">Your native language</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl mb-1">⚡</div>
                  <div className="text-sm font-medium">Instant Booking</div>
                  <div className="text-xs text-muted-foreground">Same-day sessions</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
